'use client';
import React, { useEffect, useState } from 'react';
import { X, Sparkles, ArrowRight, Dna } from 'lucide-react';
import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';
import { ALLOW_PUBLIC_REGISTRATION } from '@/lib/registrationConfig';

interface GuestDreamModalProps {
  dream: string;
  onClose: () => void;
}

export const GUEST_DREAM_KEY = 'aimaker_guest_dream';

export function saveGuestDream(dream: string) {
  if (typeof window === 'undefined') return;
  try { sessionStorage.setItem(GUEST_DREAM_KEY, dream); } catch {}
}

export function getGuestDream(): string {
  if (typeof window === 'undefined') return '';
  try { return sessionStorage.getItem(GUEST_DREAM_KEY) || ''; } catch { return ''; }
}

export function clearGuestDream() {
  if (typeof window === 'undefined') return;
  try { sessionStorage.removeItem(GUEST_DREAM_KEY); } catch {}
}

export default function GuestDreamModal({ dream, onClose }: GuestDreamModalProps) {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Save dream before showing modal
    saveGuestDream(dream);
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, [dream]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const labels = {
    en: {
      title: 'Great idea! Let\'s build it together.',
      subtitle: 'Create your free account to save your DreamDNA™ and continue building your business.',
      dreamLabel: 'Your Dream:',
      createAccount: 'Create Account',
      signIn: 'Sign In',
      note: 'Your dream will be saved and restored after you sign in.',
    },
    pt: {
      title: 'Ótima ideia! Vamos construir juntos.',
      subtitle: 'Crie sua conta gratuita para salvar seu DreamDNA™ e continuar construindo seu negócio.',
      dreamLabel: 'Seu Sonho:',
      createAccount: 'Criar Conta',
      signIn: 'Entrar',
      note: 'Seu sonho será salvo e restaurado após o login.',
    },
    es: {
      title: '¡Gran idea! Construyámosla juntos.',
      subtitle: 'Crea tu cuenta gratuita para guardar tu DreamDNA™ y continuar construyendo tu negocio.',
      dreamLabel: 'Tu Sueño:',
      createAccount: 'Crear Cuenta',
      signIn: 'Iniciar Sesión',
      note: 'Tu sueño se guardará y restaurará después de iniciar sesión.',
    },
  };

  const l = labels[language as keyof typeof labels] ?? labels.en;
  const authBase = `/sign-up-login-screen?redirect=${encodeURIComponent('/dream-workspace')}&dream=${encodeURIComponent(dream)}`;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-border shadow-2xl shadow-black/50 overflow-hidden"
        style={{
          background: 'rgba(15,15,25,0.98)',
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(16px)',
          transition: 'transform 0.3s ease',
        }}
      >
        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors z-10"
        >
          <X size={16} />
        </button>

        {/* Header gradient */}
        <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-mint" />

        <div className="p-8">
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/30 flex items-center justify-center mb-5 mx-auto">
            <Dna size={24} className="text-primary" />
          </div>

          {/* Title */}
          <h2 className="text-xl font-800 text-foreground text-center mb-2">{l.title}</h2>
          <p className="text-sm text-muted-foreground text-center mb-6 leading-relaxed">{l.subtitle}</p>

          {/* Dream preview */}
          <div className="mb-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={13} className="text-primary" />
              <span className="text-xs font-600 text-primary">{l.dreamLabel}</span>
            </div>
            <p className="text-sm text-foreground leading-relaxed italic">"{dream}"</p>
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            {ALLOW_PUBLIC_REGISTRATION ? (
              <Link
                href={`${authBase}&tab=signup`}
                className="w-full flex items-center justify-center gap-2 btn-primary py-3 rounded-xl text-sm font-700 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <Sparkles size={15} />
                {l.createAccount}
                <ArrowRight size={14} />
              </Link>
            ) : (
              <Link
                href="/private-beta"
                className="w-full flex items-center justify-center gap-2 btn-primary py-3 rounded-xl text-sm font-700 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <Sparkles size={15} />
                Join Waitlist
                <ArrowRight size={14} />
              </Link>
            )}
            <Link
              href={`${authBase}&mode=signin`}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-600 border border-border text-foreground hover:bg-muted/20 transition-all"
            >
              {l.signIn}
            </Link>
          </div>

          {/* Note */}
          <p className="text-center text-xs text-muted-foreground/60 mt-4">{l.note}</p>
        </div>
      </div>
    </div>
  );
}
